import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { updateCart, clearCart } from "../../redux/cartRedux"
import { updateUser, createOrder } from "../../redux/apiCalls"
import { useNavigate } from "react-router-dom"
// Components
import Button from "../Button/Button"
import Checkbox from "../Checkbox/Checkbox"
// Styles
import { Wrapper, Title, Form, StyledInputs, FormItem, StyledInput, StyledLabel, StyledTextArea, Message } from "./CartAddress.styles"
import { useTheme } from "styled-components"

const CartAddress = ({ display, role, validated }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const theme = useTheme()
    const { t } = useTranslation()
    const cart = useSelector((state) => state.cart)
    const user = useSelector((state) => state.user.currentUser)
    const [address, setAddress] = useState({
        name: "",
        street: "",
        zipCode: "",
        city: "",
        region: "",
        country: "",
        info: ""
    })
    const [addressSave, setAddressSave] = useState(false)
    const [sameAddress, setSameAddress] = useState(true)
    const [message, setMessage] = useState(null)

    useEffect(() => {
        if (user) {
            (role === "delivery") ? setAddress(user.deliveryAddress) : setAddress(user.invoiceAddress)
        }
    }, [role, user])

    const handleChange = (e) => {
        setAddress((prev) => {
            return { ...prev, [e.target.name]: e.target.value }
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        let paymentMethod = ""
        if ((user?.type === "public" || user === null) &&
            (role === "invoice" || sameAddress)) {
            paymentMethod = "card"
        }

        if (role === "delivery") {
            dispatch(updateCart({
                userId: user?._id,
                discount: user?.discount,
                deliveryAddress: {
                    name: address.name,
                    street: address.street,
                    zipCode: address.zipCode,
                    city: address.city,
                    region: address.region,
                    country: address.country,
                    info: address.info
                },
                sameAddress: sameAddress,
                paymentMethod: paymentMethod,
                shippingMethod: "",
                shippingPrice: 0
            }))
            addressSave && await updateUser(user._id, { ...user, deliveryAddress: address }, dispatch)

        } else {
            dispatch(updateCart({
                userId: user?._id,
                discount: user?.discount,
                deliveryAddress: cart.deliveryAddress,
                invoiceAddress: {
                    name: address.name,
                    street: address.street,
                    zipCode: address.zipCode,
                    city: address.city,
                    region: address.region,
                    country: address.country,
                    info: address.info
                },
                sameAddress: sameAddress,
                paymentMethod: paymentMethod,
                shippingMethod: "",
                shippingPrice: 0
            }))
            addressSave && await updateUser(user._id, { ...user, invoiceAddress: address }, dispatch)
        }

        (user?.type === "optician" || user?.type === "distributor") && validateOrder()
    }

    const validateOrder = async () => {
        let cartProducts = []
        for (let index = 0; index < cart.products.length; index++) {
            cartProducts.push({
                "code": cart.products[index].code,
                "detailedColor": cart.products[index].detailedColor,
                "ean": cart.products[index].ean,
                "inventory": cart.products[index].inventory,
                "outOfCollection": cart.products[index].outOfCollection,
                "collectionSeason": cart.products[index].collectionSeason,
                "model": cart.products[index].model,
                "size": cart.products[index].size,
                "quantity": cart.products[index].quantity,
                "unitPrice": cart.products[index].unitPrice,
                "info": "",
                "imageUrl": theme === "dark" ? cart.products[index].imagesDark[0] : cart.products[index].imagesLight[0]
            })
        }

        const res = await createOrder({
            "userId": cart.userId,
            "products": cartProducts,
            totalQuantity: cart.totalQuantity,
            discount: cart.discount,
            shippingMethod: cart.shippingMethod,
            shippingPrice: cart.shippingPrice,
            amount: cart.amount,
            deliveryAddress: cart.deliveryAddress,
            invoiceAddress: cart.invoiceAddress,
            status: "Pending",
            payment: null,
            payed: false,
            commentInternal: "",
            commentForCustomer: "",
            carrierUrl: ""
        }, dispatch)

        if (res === "success") {
            setMessage(t("checkoutForm.message.m1"))
            dispatch(clearCart())
            setTimeout(() => navigate("/account"), 4000)
        } else {
            setMessage(t("checkoutForm.message.m4"))
        }
    }

    return (
        <Wrapper display={display ? "flex" : "none"}>
            <Title>{(role === "delivery") ? t("cartAddress.title.t1") : t("cartAddress.title.t2")}</Title>
            <Form>
                <StyledInputs>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l1")}</StyledLabel>
                        <StyledInput type="text" name="name" defaultValue={address?.name} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l2")}</StyledLabel>
                        <StyledInput type="text" name="street" defaultValue={address?.street} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l3")}</StyledLabel>
                        <StyledInput type="text" name="zipCode" defaultValue={address?.zipCode} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l4")}</StyledLabel>
                        <StyledInput type="text" name="city" defaultValue={address?.city} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l5")}</StyledLabel>
                        <StyledInput type="text" name="region" defaultValue={address?.region} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l6")}</StyledLabel>
                        <StyledInput type="text" name="country" defaultValue={address?.country} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <FormItem>
                        <StyledLabel>{t("cartAddress.label.l7")}</StyledLabel>
                        <StyledTextArea name="info" defaultValue={address?.info} onChange={handleChange} readOnly={validated} />
                    </FormItem>
                    <div hidden={validated || !user}>
                        <FormItem>
                            <StyledLabel>
                                <Checkbox checked={addressSave} onChange={() => setAddressSave((current) => !current)} />
                                {t("cartAddress.label.l8")}
                            </StyledLabel>
                        </FormItem>
                    </div>
                    <div hidden={validated || role === "invoice"}>
                        <FormItem>
                            <StyledLabel>
                                <Checkbox checked={sameAddress} onChange={() => setSameAddress((current) => !current)} />
                                {t("cartAddress.label.l9")}
                            </StyledLabel>
                        </FormItem>
                    </div>
                </StyledInputs>
                <div hidden={validated}>
                    <Button
                        text={(user?.type === "optician" || user?.type === "distributor")
                            ? t("cartAddress.button.b1")
                            : t("cartAddress.button.b2")}
                        callback={(e) => handleSubmit(e)}
                        submit
                    />
                    {message && <Message>{message}</Message>}
                </div>
            </Form>
        </Wrapper>
    )
}

CartAddress.propTypes = {
    display: PropTypes.bool,
    role: PropTypes.string,
    validated: PropTypes.bool
}

export default CartAddress
